import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const HERO_SELECTOR = '[data-deferred-hero-video]';
const VIDEO_SELECTOR = 'video[data-hero-video]';
const PREFETCH_DISTANCE = 800;
const PREFETCH_ROOT_MARGIN = `${PREFETCH_DISTANCE}px 0px`;
const controllers = new WeakMap();

class HeroVideoController {
  /** @param {HTMLElement} hero */
  constructor(hero) {
    this.hero = hero;
    this.videos = Array.from(hero.querySelectorAll(VIDEO_SELECTOR));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.designMode = hero.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
    this.isNearViewport = false;
    this.isVisible = false;

    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleFallbackViewportChange = this.handleFallbackViewportChange.bind(this);
    this.handleScrollContainerChange = this.handleScrollContainerChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connect() {
    if (this.videos.length === 0) return;

    for (const video of this.videos) {
      video.autoplay = false;
      video.preload = 'none';
    }

    this.addMediaQueryListener(this.reducedMotion, this.handleReducedMotionChange);
    this.addMediaQueryListener(this.mobileMedia, this.handleBreakpointChange);
    this.addMediaQueryListener(scrollContainerMediaQuery, this.handleScrollContainerChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.designMode) {
      this.isNearViewport = true;
      this.isVisible = true;
      this.sync();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.setupObservers();
    } else {
      this.setupFallback();
    }
  }

  setupObservers() {
    this.teardownFallback();
    this.prefetchObserver?.disconnect();
    this.visibilityObserver?.disconnect();

    const root = getIntersectionRoot();

    this.prefetchObserver = new IntersectionObserver(
      ([entry]) => {
        this.isNearViewport = entry?.isIntersecting ?? false;
        if (this.isNearViewport) this.sync();
      },
      { root, rootMargin: PREFETCH_ROOT_MARGIN }
    );

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry?.isIntersecting ?? false;
        this.sync();
      },
      { root, threshold: 0.01 }
    );

    this.prefetchObserver.observe(this.hero);
    this.visibilityObserver.observe(this.hero);
  }

  setupFallback() {
    this.teardownFallback();
    this.fallbackScrollTarget = getScrollEventTarget();
    this.fallbackScrollTarget.addEventListener('scroll', this.handleFallbackViewportChange, { passive: true });
    window.addEventListener('resize', this.handleFallbackViewportChange, { passive: true });
    this.updateFallbackViewportState();
  }

  teardownFallback() {
    this.fallbackScrollTarget?.removeEventListener('scroll', this.handleFallbackViewportChange);
    window.removeEventListener('resize', this.handleFallbackViewportChange);
    if (this.fallbackAnimationFrame != null) cancelAnimationFrame(this.fallbackAnimationFrame);
    this.fallbackAnimationFrame = null;
    this.fallbackScrollTarget = null;
  }

  updateFallbackViewportState() {
    const heroRect = this.hero.getBoundingClientRect();
    const intersectionRoot = getIntersectionRoot();
    const rootRect =
      intersectionRoot instanceof HTMLElement
        ? intersectionRoot.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight };

    this.isNearViewport =
      heroRect.bottom > rootRect.top - PREFETCH_DISTANCE &&
      heroRect.top < rootRect.bottom + PREFETCH_DISTANCE;
    this.isVisible = heroRect.bottom > rootRect.top && heroRect.top < rootRect.bottom;
    this.sync();
  }

  disconnect() {
    this.prefetchObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.teardownFallback();
    this.removeMediaQueryListener(this.reducedMotion, this.handleReducedMotionChange);
    this.removeMediaQueryListener(this.mobileMedia, this.handleBreakpointChange);
    this.removeMediaQueryListener(scrollContainerMediaQuery, this.handleScrollContainerChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.pauseAll();
  }

  sync() {
    const activeVideos = this.videos.filter((video) => this.isActiveArtDirection(video));
    const activeVideoSet = new Set(activeVideos);

    for (const video of this.videos) {
      if (!activeVideoSet.has(video)) video.pause();
    }

    if (this.reducedMotion.matches || document.hidden) {
      if (this.isNearViewport || this.isVisible) {
        for (const video of activeVideos) this.preparePoster(video);
      }
      this.pauseAll();
      return;
    }

    if (this.isNearViewport) {
      for (const video of activeVideos) this.prepare(video);
    }

    if (!this.isVisible) {
      for (const video of activeVideos) video.pause();
      return;
    }

    for (const video of activeVideos) {
      this.prepare(video);
      const playPromise = video.play();
      if (playPromise instanceof Promise) playPromise.catch(() => {});
    }
  }

  /** @param {HTMLVideoElement} video */
  prepare(video) {
    this.preparePoster(video);
    if (video.dataset.heroVideoPrepared === 'true') return;

    for (const source of video.querySelectorAll('source[data-src]')) {
      const src = source.getAttribute('data-src');
      if (!src) continue;
      source.setAttribute('src', src);
      source.removeAttribute('data-src');
    }

    video.dataset.heroVideoPrepared = 'true';
    video.preload = 'auto';
    video.load();
  }

  /** @param {HTMLVideoElement} video */
  preparePoster(video) {
    const poster = video.dataset.poster;
    if (!poster) return;

    video.poster = poster;
    delete video.dataset.poster;
  }

  /** @param {HTMLVideoElement} video */
  isActiveArtDirection(video) {
    const wrapper = video.closest('.hero__media-wrapper');
    if (!(wrapper instanceof HTMLElement)) return true;
    return window.getComputedStyle(wrapper).display !== 'none';
  }

  pauseAll() {
    for (const video of this.videos) video.pause();
  }

  handleReducedMotionChange() {
    this.sync();
  }

  handleBreakpointChange() {
    this.sync();
  }

  handleFallbackViewportChange() {
    if (this.fallbackAnimationFrame != null) return;
    this.fallbackAnimationFrame = requestAnimationFrame(() => {
      this.fallbackAnimationFrame = null;
      this.updateFallbackViewportState();
    });
  }

  handleScrollContainerChange() {
    if (this.designMode) return;
    if ('IntersectionObserver' in window) {
      this.setupObservers();
    } else {
      this.setupFallback();
    }
  }

  handleVisibilityChange() {
    this.sync();
  }

  /**
   * @param {MediaQueryList} mediaQuery
   * @param {() => void} listener
   */
  addMediaQueryListener(mediaQuery, listener) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }
  }

  /**
   * @param {MediaQueryList} mediaQuery
   * @param {() => void} listener
   */
  removeMediaQueryListener(mediaQuery, listener) {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', listener);
    } else {
      mediaQuery.removeListener(listener);
    }
  }
}

/** @param {ParentNode} root */
function initialize(root = document) {
  const heroes = [];

  if (root instanceof HTMLElement && root.matches(HERO_SELECTOR)) heroes.push(root);
  heroes.push(...root.querySelectorAll(HERO_SELECTOR));

  for (const hero of heroes) {
    if (!(hero instanceof HTMLElement) || controllers.has(hero)) continue;
    const controller = new HeroVideoController(hero);
    controllers.set(hero, controller);
    controller.connect();
  }
}

/** @param {ParentNode} root */
function disconnect(root) {
  const heroes = [];

  if (root instanceof HTMLElement && root.matches(HERO_SELECTOR)) heroes.push(root);
  heroes.push(...root.querySelectorAll(HERO_SELECTOR));

  for (const hero of heroes) {
    if (!(hero instanceof HTMLElement)) continue;
    controllers.get(hero)?.disconnect();
    controllers.delete(hero);
  }
}

initialize();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initialize(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof HTMLElement) disconnect(event.target);
});
